/**
 * syncResumability.ts — DesktopPOS-only sync health & resumability layer.
 *
 * The shared `sync/index.ts` is a byte-near port of BigxBookApp's sync code,
 * so adding logic there would either drift from BigxBook or require coupled
 * edits to a frozen tree. Instead this module wraps the existing `useSync`
 * hook with three things the desktop-pilot tester cares about:
 *
 *   1. **Interrupt detection** — set a "syncInProgress" flag in electron-store
 *      before each cycle, clear it after. On startup, if the flag is still set
 *      from a previous run, we know the app died mid-sync and bump a counter.
 *      The backend already handles cursor-resume safely (it only advances
 *      `lastPulledAt` when ALL paginated pages complete), so the client side
 *      just needs to *observe* and *report* — no rollback work required.
 *
 *   2. **Persistent stats** — last attempt, last success, last error,
 *      consecutive failures, and interrupted count are persisted across
 *      app restarts via `window.api.kv` (DPAPI-protected by Electron's
 *      safeStorage when secret, plain electron-store otherwise).
 *
 *   3. **Circuit breaker** — after N consecutive failures we throttle
 *      auto-triggers (focus/network) for a cooldown window so a flapping
 *      backend doesn't burn battery / consume bandwidth. Manual sync from
 *      the UI bypasses the breaker.
 *
 * All keys live under the `__sync__` prefix in electron-store so they're
 * easy to audit / wipe.
 */

const KEY_STATS       = '__sync__stats';
const KEY_IN_PROGRESS = '__sync__inProgress';

const MAX_CONSECUTIVE_FAILURES_BEFORE_BREAKER = 5;
const BREAKER_COOLDOWN_MS                     = 5 * 60 * 1000; // 5 min

export interface SyncStats {
  lastAttemptAt:        number | null;
  lastSuccessAt:        number | null;
  lastErrorAt:          number | null;
  lastErrorMessage:     string | null;
  consecutiveFailures:  number;
  totalCycles:          number;
  totalSuccesses:       number;
  totalFailures:        number;
  /** Cycles where the app was killed before completion (bumped on next launch). */
  interruptedCount:     number;
  /** Set when circuit breaker is open; auto-triggers are throttled until this time. */
  breakerOpenUntil:     number | null;
}

const EMPTY_STATS: SyncStats = {
  lastAttemptAt:       null,
  lastSuccessAt:       null,
  lastErrorAt:         null,
  lastErrorMessage:    null,
  consecutiveFailures: 0,
  totalCycles:         0,
  totalSuccesses:      0,
  totalFailures:       0,
  interruptedCount:    0,
  breakerOpenUntil:    null,
};

// Best-effort access to the electron preload bridge. In dev/web the bridge
// may be absent — fall back to in-memory storage so the hook still works.
type KvBridge = { get: (k: string) => Promise<string | null>; set: (k: string, v: string) => Promise<true>; delete: (k: string) => Promise<true> };
function kv(): KvBridge | null {
  return (typeof window !== 'undefined' ? (window as unknown as { api?: { kv?: KvBridge } }).api?.kv : null) ?? null;
}
const _memFallback = new Map<string, string>();
async function kvGet(key: string): Promise<string | null> {
  const bridge = kv();
  if (bridge) return bridge.get(key);
  return _memFallback.get(key) ?? null;
}
async function kvSet(key: string, value: string): Promise<void> {
  const bridge = kv();
  if (bridge) { await bridge.set(key, value); return; }
  _memFallback.set(key, value);
}
async function kvDelete(key: string): Promise<void> {
  const bridge = kv();
  if (bridge) { await bridge.delete(key); return; }
  _memFallback.delete(key);
}

// ── Stats ────────────────────────────────────────────────────────────────────

export async function loadSyncStats(): Promise<SyncStats> {
  try {
    const raw = await kvGet(KEY_STATS);
    if (!raw) return { ...EMPTY_STATS };
    const parsed = JSON.parse(raw) as Partial<SyncStats>;
    return { ...EMPTY_STATS, ...parsed };
  } catch {
    return { ...EMPTY_STATS };
  }
}

async function saveSyncStats(stats: SyncStats): Promise<void> {
  try {
    await kvSet(KEY_STATS, JSON.stringify(stats));
  } catch (e) {
    console.warn('[syncResumability] Failed to persist stats:', (e as Error).message);
  }
}

export async function clearSyncStats(): Promise<void> {
  await kvDelete(KEY_STATS);
  await kvDelete(KEY_IN_PROGRESS);
}

// ── Interrupt detection (call once at app startup) ──────────────────────────

/**
 * Reconcile any sync that was in-flight when the previous run was killed.
 * Returns true if an interrupt was detected & accounted for.
 *
 * Safe to call multiple times — only the first call after an actual interrupt
 * does anything; subsequent calls are no-ops.
 */
export async function reconcileInterruptedSync(): Promise<boolean> {
  const wasInFlight = await kvGet(KEY_IN_PROGRESS);
  if (!wasInFlight) return false;

  const stats = await loadSyncStats();
  stats.interruptedCount += 1;
  // An interrupt counts as a failure for circuit-breaker purposes — repeated
  // crashes during sync should also throttle auto-triggers.
  stats.consecutiveFailures += 1;
  stats.lastErrorAt          = Date.now();
  stats.lastErrorMessage     = `App was terminated mid-sync (started ${wasInFlight})`;
  await saveSyncStats(stats);
  await kvDelete(KEY_IN_PROGRESS);
  console.warn('[syncResumability] Detected interrupted sync from previous run');
  return true;
}

// ── Cycle bookkeeping (wrap your sync call with these) ──────────────────────

export async function markCycleStart(): Promise<void> {
  await kvSet(KEY_IN_PROGRESS, new Date().toISOString());
  const stats = await loadSyncStats();
  stats.lastAttemptAt = Date.now();
  stats.totalCycles  += 1;
  await saveSyncStats(stats);
}

export async function markCycleSuccess(): Promise<void> {
  await kvDelete(KEY_IN_PROGRESS);
  const stats = await loadSyncStats();
  stats.lastSuccessAt        = Date.now();
  stats.lastErrorAt          = null;
  stats.lastErrorMessage     = null;
  stats.consecutiveFailures  = 0;
  stats.totalSuccesses      += 1;
  stats.breakerOpenUntil     = null;
  await saveSyncStats(stats);
}

export async function markCycleFailure(message: string): Promise<void> {
  await kvDelete(KEY_IN_PROGRESS);
  const stats = await loadSyncStats();
  stats.lastErrorAt           = Date.now();
  stats.lastErrorMessage      = message.slice(0, 500);
  stats.consecutiveFailures  += 1;
  stats.totalFailures        += 1;
  if (stats.consecutiveFailures >= MAX_CONSECUTIVE_FAILURES_BEFORE_BREAKER) {
    stats.breakerOpenUntil = Date.now() + BREAKER_COOLDOWN_MS;
    console.warn(
      `[syncResumability] Circuit breaker OPEN — ${stats.consecutiveFailures} consecutive failures. ` +
      `Auto-triggers throttled until ${new Date(stats.breakerOpenUntil).toLocaleTimeString()}`,
    );
  }
  await saveSyncStats(stats);
}

// ── Circuit breaker query ───────────────────────────────────────────────────

/**
 * @returns `null` if auto-triggers are allowed; otherwise the timestamp at
 *          which the breaker re-closes.
 */
export async function isAutoSyncThrottled(): Promise<number | null> {
  const stats = await loadSyncStats();
  if (!stats.breakerOpenUntil) return null;
  if (Date.now() >= stats.breakerOpenUntil) {
    // Cooldown elapsed — close the breaker (next failure will reopen).
    stats.breakerOpenUntil = null;
    await saveSyncStats(stats);
    return null;
  }
  return stats.breakerOpenUntil;
}

/** Manually reset the breaker — used by "Sync now" button in Settings. */
export async function resetCircuitBreaker(): Promise<void> {
  const stats = await loadSyncStats();
  stats.breakerOpenUntil    = null;
  stats.consecutiveFailures = 0;
  await saveSyncStats(stats);
}
